import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../services/AuthService';

const TIMEOUT_MS      = 60 * 60 * 1000; // 60 minutes
const WARNING_MS      = 55 * 60 * 1000; // warn at 55 minutes
const TICK_INTERVAL   = 10 * 1000;      // check every 10 s

/**
 * useSessionTimeout
 * Tracks user activity (mouse, keyboard, touch).
 * Warns at 25 min of inactivity, force-logs-out at 30 min.
 *
 * Returns:
 *   showWarning   – boolean: show the "Still there?" banner
 *   secondsLeft   – number: seconds until forced logout (when warning is active)
 *   resetTimer()  – call when user clicks "Stay logged in"
 */
export function useSessionTimeout() {
  const navigate       = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const [showWarning, setShowWarning]   = useState(false);
  const [secondsLeft, setSecondsLeft]   = useState(0);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
  }, []);

  // Record activity
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      if (showWarning) setShowWarning(false);
    };
    events.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, onActivity));
  }, [showWarning]);

  // Inactivity checker
  useEffect(() => {
    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;

      if (idle >= TIMEOUT_MS) {
        clearInterval(interval);
        logout();
        navigate('/', { replace: true });
        return;
      }

      if (idle >= WARNING_MS) {
        const remaining = Math.ceil((TIMEOUT_MS - idle) / 1000);
        setSecondsLeft(remaining);
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    }, TICK_INTERVAL);

    return () => clearInterval(interval);
  }, [navigate]);

  return { showWarning, secondsLeft, resetTimer };
}
